import {
  VesselArea,
  VesselSpeedTag,
  VesselSpeedType,
} from '@seaber/common/graphql/types';
import type { BunkerGradeSelectionPreference } from './Core';
import { type PortCallBunkerOverride } from './Core';
import type { VSCSByTagTypeAndArea, VSCSByTypeAndArea } from './Vessel';
import type { OrgDefaultBunkerGradeIdsByArea } from '../../lib/org-area-bunker-grade.utils';
import { isOrgDefaultBunkerGradeArea } from '../../lib/org-area-bunker-grade.utils';

interface ResolveBunkerGradeIdParams {
  portBunkerOverrides: readonly PortCallBunkerOverride[] | null;
  vesselArea: VesselArea;
  type: VesselSpeedType;
  isExtraPortConsumptionHours?: boolean;
  isCargoEvent?: boolean;
  isLoading?: boolean;
  speedConsumptionsByTag: VSCSByTagTypeAndArea;
  orgDefaultBunkerGradeIdsByArea?: OrgDefaultBunkerGradeIdsByArea | null;
  vesselDefaultBunkerGradeId?: number | null;
}

/** Most prefereable to least preferable matching */
const LEG_BUNKER_GRADE_PREFERENCES: BunkerGradeSelectionPreference[] = [
  // First try vessel default if set and available for this type/area, then DEFAULT speed row for the area,
  // then organization-level defaults (if the vessel speed consumptions table has that grade), then ALL_AREAS, and lastly any speed row
  { tag: null, area: 'vessel_default' },
  { tag: VesselSpeedTag.DEFAULT, area: 'match' },
  { tag: null, area: 'org_default_for_area' },
  { tag: VesselSpeedTag.DEFAULT, area: VesselArea.ALL_AREAS },
  { tag: null, area: 'match' },
  { tag: null, area: VesselArea.ALL_AREAS },
];
/** Most prefereable to least preferable matching */
const PORT_BUNKER_GRADE_PREFERENCES: BunkerGradeSelectionPreference[] = [
  // First try vessel default if set and available for this type/tag/area, then matching tag, org default, default, then null
  { tag: 'match', area: 'vessel_default' },
  { tag: 'match', area: 'match' },
  { tag: 'match', area: VesselArea.ALL_AREAS },
  { tag: VesselSpeedTag.DEFAULT, area: 'match' },
  { tag: VesselSpeedTag.DEFAULT, area: VesselArea.ALL_AREAS },
  { tag: null, area: 'match' },
  { tag: null, area: VesselArea.ALL_AREAS },
];

export function resolveBunkerGradeId({
  portBunkerOverrides,
  vesselArea,
  type,
  isExtraPortConsumptionHours,
  isCargoEvent,
  isLoading,
  speedConsumptionsByTag,
  orgDefaultBunkerGradeIdsByArea,
  vesselDefaultBunkerGradeId,
}: ResolveBunkerGradeIdParams): {
  bunkerGradeId: number | undefined;
  tag: VesselSpeedTag | 'leg';
} {
  const overrideBunkerGrade = portBunkerOverrides?.find(
    (i) =>
      (i.area === vesselArea ||
        // When port overridden we know it means exactly this port - area doesn't matter
        type === VesselSpeedType.PORT) &&
      i.type === type &&
      (isExtraPortConsumptionHours
        ? i.tag === VesselSpeedTag.IDLE
        : i.tag !== VesselSpeedTag.IDLE),
  );

  const tag =
    type === VesselSpeedType.PORT
      ? isCargoEvent
        ? isLoading
          ? VesselSpeedTag.LOADING
          : VesselSpeedTag.DISCHARGING
        : VesselSpeedTag.IDLE
      : 'leg';

  let bunkerGradeId = overrideBunkerGrade?.bunker_grade_id;

  // If undefined: no override in use, look up a suitable grade from the table
  if (bunkerGradeId === undefined) {
    /** We use the first combination that we can find a value for */
    const gradeSelectionPreferences: BunkerGradeSelectionPreference[] =
      tag === 'leg'
        ? LEG_BUNKER_GRADE_PREFERENCES
        : PORT_BUNKER_GRADE_PREFERENCES;

    // Get the first match
    for (const preference of gradeSelectionPreferences) {
      const preferenceTag = preference.tag === 'match' ? tag : preference.tag;

      if (preferenceTag === 'leg') {
        // There are expected no 'match' tag values to exist in the leg preferences array
        throw new Error(
          `Unexpected speed and preference tag combination: ${preference.tag}-${tag}`,
        );
      }

      // Handle vessel default bunker grade preference
      if (preference.area === 'vessel_default') {
        if (!vesselDefaultBunkerGradeId) {
          continue;
        }
        const speedConsumptions = constructSpeedConsumptions(
          speedConsumptionsByTag,
        );
        const consumptionsForType = speedConsumptions.get(type);

        // Check if vessel default is available for the wanted area or ALL_AREAS
        // For port calls, also check that the tag matches
        const hasVesselDefaultForArea = consumptionsForType
          ?.get(vesselArea)
          ?.some(
            (sc) =>
              sc.bunkerGradeOverrideByBunkerGradeId?.id ===
                vesselDefaultBunkerGradeId &&
              (tag === 'leg' || sc.tag === preferenceTag),
          );

        const hasVesselDefaultForAllAreas = consumptionsForType
          ?.get(VesselArea.ALL_AREAS)
          ?.some(
            (sc) =>
              sc.bunkerGradeOverrideByBunkerGradeId?.id ===
                vesselDefaultBunkerGradeId &&
              (tag === 'leg' || sc.tag === preferenceTag),
          );

        if (hasVesselDefaultForArea || hasVesselDefaultForAllAreas) {
          bunkerGradeId = vesselDefaultBunkerGradeId;
          break;
        }
        continue;
      }

      const preferenceArea =
        preference.area === 'match' ||
        preference.area === 'org_default_for_area'
          ? vesselArea
          : preference.area;

      if (
        preference.area === 'org_default_for_area' &&
        isOrgDefaultBunkerGradeArea(vesselArea)
      ) {
        const orgDefaultGradeForArea =
          orgDefaultBunkerGradeIdsByArea?.[vesselArea];

        if (orgDefaultGradeForArea) {
          // we want to set bunker grade to org default for area, if it is found
          // in the speed consumptions for the vessel. if it's not found, we can't match
          // a correct speed & consumption based on the grade.
          const speedConsumptions = constructSpeedConsumptions(
            speedConsumptionsByTag,
          );
          const hasOrgDefaultAsAllAreas = speedConsumptions
            .get(type)
            ?.get(VesselArea.ALL_AREAS)
            ?.some(
              (sc) =>
                sc.bunkerGradeOverrideByBunkerGradeId?.id ===
                orgDefaultGradeForArea,
            );

          if (hasOrgDefaultAsAllAreas) {
            bunkerGradeId = orgDefaultGradeForArea;
            break;
          }
        }
      }

      bunkerGradeId = speedConsumptionsByTag
        .get(preferenceTag)
        ?.get(type)
        ?.get(preferenceArea)
        ?.at(0)?.bunkerGradeOverrideByBunkerGradeId?.id;
      if (bunkerGradeId !== undefined) {
        break;
      }
    }
  }
  return { bunkerGradeId, tag };
}

function constructSpeedConsumptions(
  speedConsumptionsByTag: VSCSByTagTypeAndArea,
): VSCSByTypeAndArea {
  const speedConsumptions: VSCSByTypeAndArea = new Map();

  speedConsumptionsByTag.forEach((typeAndAreaMap) => {
    typeAndAreaMap.forEach((areaMap, type) => {
      if (!speedConsumptions.has(type)) {
        speedConsumptions.set(type, new Map());
      }
      const existingAreaMap = speedConsumptions.get(type)!;

      areaMap.forEach((consumptions, area) => {
        if (!existingAreaMap.has(area)) {
          existingAreaMap.set(area, []);
        }
        const existingConsumptions = existingAreaMap.get(area)!;
        existingConsumptions.push(...consumptions);
      });
    });
  });

  return speedConsumptions;
}
